import { ref, onMounted, onUnmounted, readonly } from 'vue'
import { NotificationService } from '@/modules/common/services/websocket/NotificationService'
import { useMercure } from './useMercure'
import { NotificationPriority } from '@/modules/common/services/websocket/types'
import { apiClient } from '@/shared/api/client'
import {
  isServerNotificationId,
  mapServerNotification,
  serverNotificationId,
  type ServerNotification,
} from '@/modules/common/services/websocket/serverNotifications'
import type {
  Notification,
  NotificationType,
  UnsubscribeFunction,
} from '@/modules/common/services/websocket/types'

// Global notification service instance
let globalNotificationService: NotificationService | null = null

export function useRealTimeNotifications() {
  const notifications = ref<Notification[]>([])
  const unreadCount = ref(0)
  const { subscribe } = useMercure()

  let unsubscribeFromService: (() => void) | null = null
  let unsubscribeFromMercure: UnsubscribeFunction | null = null

  const getService = (): NotificationService => {
    if (!globalNotificationService) {
      globalNotificationService = new NotificationService()
    }
    return globalNotificationService
  }

  const setupSubscriptions = () => {
    const service = getService()

    // Subscribe to notification service updates
    unsubscribeFromService = service.subscribe((newNotifications) => {
      notifications.value = newNotifications
      unreadCount.value = service.getUnreadCount()
    })

    // Subscribe to Mercure notification events
    unsubscribeFromMercure = subscribe('notification', (event) => {
      const payload = (event.data || {}) as {
        title?: string
        message?: string
        type?: NotificationType
        additional_data?: Record<string, unknown>
      }

      service.addNotification(
        payload.title || 'Уведомление',
        payload.message || '',
        payload.type || 'info',
        NotificationPriority.NORMAL,
        payload.additional_data,
      )
    })

    // Subscribe to system notifications
    const unsubscribeSystem = subscribe('system_notification', (event) => {
      const payload = (event.data || {}) as {
        title?: string
        message?: string
        type?: NotificationType
      }

      service.addNotification(
        payload.title || 'Системное уведомление',
        payload.message || '',
        payload.type || 'system',
        NotificationPriority.HIGH,
      )
    })

    // Return combined unsubscribe function
    return () => {
      unsubscribeFromService?.()
      unsubscribeFromMercure?.()
      unsubscribeSystem()
    }
  }

  /** Серверное состояние обновляем без ожидания: локальный центр отвечает мгновенно. */
  const syncToServer = (request: () => Promise<unknown>): void => {
    request().catch((error) => console.warn('Не удалось синхронизировать уведомления', error))
  }

  const loadFromServer = async (): Promise<void> => {
    try {
      const response = await apiClient.get('/api/notifications', { params: { limit: 50 } })
      const items = (response.data?.data ?? []) as ServerNotification[]
      getService().hydrate(items.map(mapServerNotification))
    } catch (error) {
      console.warn('Не удалось загрузить уведомления', error)
    }
  }

  const markAsRead = (notificationId: string): void => {
    getService().markAsRead(notificationId)
    if (isServerNotificationId(notificationId)) {
      syncToServer(() => apiClient.post(`/api/notifications/${serverNotificationId(notificationId)}/read`))
    }
  }

  const markAllAsRead = (): void => {
    getService().markAllAsRead()
    syncToServer(() => apiClient.post('/api/notifications/mark-all-read', {}))
  }

  const removeNotification = (notificationId: string): void => {
    getService().removeNotification(notificationId)
    if (isServerNotificationId(notificationId)) {
      syncToServer(() => apiClient.delete(`/api/notifications/${serverNotificationId(notificationId)}`))
    }
  }

  const clearAll = (): void => {
    getService().clearAll()
  }

  const clearRead = (): void => {
    getService().clearRead()
  }

  const addNotification = (
    title: string,
    message: string,
    type?: NotificationType,
    priority?: NotificationPriority,
    data?: Record<string, unknown>,
    actionUrl?: string,
    actionText?: string,
  ): Notification => {
    // Avoid referencing runtime enums; fall back to 'info' if type is not provided
    const safeType = type ?? ('info' as NotificationType)
    return getService().addNotification(
      title,
      message,
      safeType,
      // priority default will be handled by service implementation
      priority as NotificationPriority,
      data,
      actionUrl,
      actionText,
    )
  }

  const getNotificationsByType = (type: NotificationType): Notification[] => {
    return getService().getNotificationsByType(type)
  }

  const requestPermission = async (): Promise<boolean> => {
    return await getService().requestPermission()
  }

  // Auto-cleanup expired notifications
  const startCleanupTimer = () => {
    const cleanup = () => {
      getService().cleanupExpired()
    }

    // Cleanup every 5 minutes
    const interval = setInterval(cleanup, 5 * 60 * 1000)

    return () => clearInterval(interval)
  }

  // In test environment, initialize subscriptions immediately (no component lifecycle)
  if (import.meta.env['VITEST']) {
    setupSubscriptions()
  }

  onMounted(() => {
    const unsubscribeAll = setupSubscriptions()
    const stopCleanup = startCleanupTimer()

    // Request notification permission on first mount
    requestPermission()

    // Cleanup on unmount
    onUnmounted(() => {
      unsubscribeAll()
      stopCleanup()
    })
  })

  return {
    // State
    notifications: readonly(notifications),
    unreadCount: readonly(unreadCount),

    // Methods
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAll,
    clearRead,
    addNotification,
    getNotificationsByType,
    requestPermission,
    loadFromServer,

    // Service access
    getService,
  }
}
